import {isNil} from 'ramda';

import {Base} from '../base/Base';
import type {ViewPartVariants} from '../CarDiagram.types';
import {AirCoolingSystem} from '../parts/engine/AirCoolingSystem';
import {Battery} from '../parts/engine/Battery';
import {BrakeFluid} from '../parts/engine/BrakeFluid';
import {ClutchFluid} from '../parts/engine/ClutchFluid';
import {Condensator} from '../parts/engine/Condensator';
import {EngineBlock} from '../parts/engine/EngineBlock';
import {EngineCoolant} from '../parts/engine/EngineCoolant';
import {EngineOilReservoir} from '../parts/engine/EngineOilReservoir';
import {Filters} from '../parts/engine/Filters';
import {Floor} from '../parts/engine/Floor';
import {LeftUpperSuspensionCover} from '../parts/engine/LeftUpperSuspensionCover';
import {Pipes} from '../parts/engine/Pipes';
import {PowerSteeringFluid} from '../parts/engine/PowerSteeringFluid';
import {RightUpperSuspensionCover} from '../parts/engine/RightUpperSuspensionCover';
import {SomePartA} from '../parts/engine/SomePartA';
import {SomePartB} from '../parts/engine/SomePartB';
import {WindscreenWashFluid} from '../parts/engine/WindscreenWashFluid';
import {FrontBumper} from '../parts/exterior/FrontBumper';
import {FrontLeftHeadlight} from '../parts/exterior/FrontLeftHeadlight';
import {FrontLeftSidePanel} from '../parts/exterior/FrontLeftSidePanel';
import {FrontRightHeadlight} from '../parts/exterior/FrontRightHeadlight';
import {FrontRightSidePanel} from '../parts/exterior/FrontRightSidePanel';
import {Grille} from '../parts/exterior/Grille';
import {RearBumper} from '../parts/exterior/RearBumper';
import {RearLeftTaillight} from '../parts/exterior/RearLeftTaillight';
import {RearRightTaillight} from '../parts/exterior/RearRightTaillight';
import {TowBar} from '../parts/exterior/TowBar';
import {AntiRollBar} from '../parts/mechanics/AntiRollBar';
import {DriveShaft} from '../parts/mechanics/DriveShaft';
import {EngineMechanics} from '../parts/mechanics/Engine';
import {ExhaustSystem} from '../parts/mechanics/ExhaustSystem';
import {FrontLeftBrake} from '../parts/mechanics/FrontLeftBrake';
import {FrontLeftShockAbsorber} from '../parts/mechanics/FrontLeftShockAbsorber';
import {FrontLeftSideshaft} from '../parts/mechanics/FrontLeftSideshaft';
import {FrontLeftUpperControlArm} from '../parts/mechanics/FrontLeftUpperControlArm';
import {FrontLeftWheel} from '../parts/mechanics/FrontLeftWheel';
import {FrontRightBrake} from '../parts/mechanics/FrontRightBrake';
import {FrontRightShockAbsorber} from '../parts/mechanics/FrontRightShockAbsorber';
import {FrontRightSideshaft} from '../parts/mechanics/FrontRightSideshaft';
import {FrontRightUpperControlArm} from '../parts/mechanics/FrontRightUpperControlArm';
import {FrontRightWheel} from '../parts/mechanics/FrontRightWheel';
import {GasTank} from '../parts/mechanics/GasTank';
import {RearDifferencial} from '../parts/mechanics/RearDifferencial';
import {RearLeftBrake} from '../parts/mechanics/RearLeftBrake';
import {RearLeftShockAbsorber} from '../parts/mechanics/RearLeftShockAbsorber';
import {RearLeftSideshaft} from '../parts/mechanics/RearLeftSideshaft';
import {RearLeftUpperControlArm} from '../parts/mechanics/RearLeftUpperControlArm';
import {RearLeftWheel} from '../parts/mechanics/RearLeftWheel';
import {RearRightBrake} from '../parts/mechanics/RearRightBrake';
import {RearRightShockAbsorber} from '../parts/mechanics/RearRightShockAbsorber';
import {RearRightSideshaft} from '../parts/mechanics/RearRightSideshaft';
import {RearRightUpperControlArm} from '../parts/mechanics/RearRightUpperControlArm';
import {RearRightWheel} from '../parts/mechanics/RearRightWheel';
import {SteeringBar} from '../parts/mechanics/SteeringBar';
import {TorsenDifferencial} from '../parts/mechanics/TorsenDifferencial';
import {TorsionBar} from '../parts/mechanics/TorsionBar';
import {Transmission} from '../parts/mechanics/Transmission';

interface EngineViewProps {
  readonly partVariants: ViewPartVariants;
  readonly onPartClick?: (partId: string) => void;
}

export function EngineView(props: EngineViewProps) {
  const onPartClick = props.onPartClick;
  const clickable = (partId: string): (() => void) | undefined => {
    if (isNil(onPartClick)) {
      return undefined;
    }
    return () => onPartClick(partId);
  };

  return (
    <div className="relative h-[502px] w-[1342px]">
      <Base />
      {/* SHARED MECHANICS - background */}
      <ExhaustSystem />
      <FrontBumper />
      <FrontRightHeadlight />
      <FrontLeftHeadlight />
      <Grille />
      <RearBumper />
      <RearRightTaillight />
      <RearLeftTaillight />
      <TowBar />
      <SteeringBar />
      <FrontRightWheel />
      <FrontRightBrake />
      <FrontLeftWheel />
      <FrontLeftBrake />
      <FrontRightUpperControlArm />
      <FrontLeftUpperControlArm />
      <FrontRightSideshaft />
      <FrontLeftSideshaft />
      <FrontRightShockAbsorber />
      <FrontLeftShockAbsorber />
      <EngineMechanics />
      <TorsenDifferencial />
      <Transmission />
      <TorsionBar />
      <RearRightWheel />
      <RearLeftWheel />
      <RearRightBrake />
      <RearLeftBrake />
      <RearRightUpperControlArm />
      <RearLeftUpperControlArm />
      <RearRightSideshaft />
      <RearLeftSideshaft />
      <RearRightShockAbsorber />
      <RearLeftShockAbsorber />
      <DriveShaft />
      <RearDifferencial />
      <AntiRollBar />
      <GasTank />
      <FrontRightSidePanel />
      <FrontLeftSidePanel />
      {/* ENGINE SPECIFIC PARTS */}
      <Floor />
      <Pipes variant={props.partVariants['pipes'] ?? 'default'} onClick={clickable('pipes')} />
      <AirCoolingSystem
        variant={props.partVariants['air-cooling-system'] ?? 'default'}
        onClick={clickable('air-cooling-system')}
      />
      <LeftUpperSuspensionCover
        variant={props.partVariants['left-upper-suspension-cover'] ?? 'default'}
        onClick={clickable('left-upper-suspension-cover')}
      />
      <RightUpperSuspensionCover
        variant={props.partVariants['right-upper-suspension-cover'] ?? 'default'}
        onClick={clickable('right-upper-suspension-cover')}
      />
      <Condensator
        variant={props.partVariants['condensator'] ?? 'default'}
        onClick={clickable('condensator')}
      />
      <EngineBlock
        variant={props.partVariants['engine-block'] ?? 'default'}
        onClick={clickable('engine-block')}
      />
      <WindscreenWashFluid
        variant={props.partVariants['windscreen-wash-fluid'] ?? 'default'}
        onClick={clickable('windscreen-wash-fluid')}
      />
      <SomePartA
        variant={props.partVariants['some-part-a'] ?? 'default'}
        onClick={clickable('some-part-a')}
      />
      <SomePartB
        variant={props.partVariants['some-part-b'] ?? 'default'}
        onClick={clickable('some-part-b')}
      />
      <EngineOilReservoir
        variant={props.partVariants['engine-oil-reservoir'] ?? 'default'}
        onClick={clickable('engine-oil-reservoir')}
      />
      <PowerSteeringFluid
        variant={props.partVariants['power-steering-fluid'] ?? 'default'}
        onClick={clickable('power-steering-fluid')}
      />
      <EngineCoolant
        variant={props.partVariants['engine-coolant'] ?? 'default'}
        onClick={clickable('engine-coolant')}
      />
      <Filters
        variant={props.partVariants['filters'] ?? 'default'}
        onClick={clickable('filters')}
      />
      <Battery
        variant={props.partVariants['battery'] ?? 'default'}
        onClick={clickable('battery')}
      />
      <BrakeFluid
        variant={props.partVariants['brake-fluid'] ?? 'default'}
        onClick={clickable('brake-fluid')}
      />
      <ClutchFluid
        variant={props.partVariants['clutch-fluid'] ?? 'default'}
        onClick={clickable('clutch-fluid')}
      />
    </div>
  );
}
